import React, { useMemo, useState, useEffect } from "react";
import { WBMCard } from "wbm-card";
import { Button } from "wbm-inputs";
import {
  ProductAvailability,
  ProductImage,
  ProductName,
  ProductPrice,
  QtyBreaksDropdown,
  QuantityBreak,
  AddToCart,
} from "wbm-generic-components";
import CustomizeButton from "./CustomizeButton.js";
import ConfigProps from "../constants/configProps.js";
import {
  LineLevelCouponDiscDisplay,
  PurchaseProcess,
} from "../constants/enums.js";
import { IProductInfo } from "../interfaces/IProductInfo.js";
import { IItemAvailability } from "../interfaces/IItemAvailability.js";
import { IIndicatorIcon } from "../interfaces/IIndicatorIcon.js";
import { IQuantityBreak } from "../interfaces/IQuantityBreak.js";
import { IIndividualUnitData } from "../interfaces/IIndividualUnitData.js";
import { ISupercedeProduct } from "@wbmason/cart-service/interfaces/supercedeProduct.js";
import { IPricedProduct } from "@wbmason/cart-service/interfaces/pricedProduct.js";
import { ICartItem } from "@wbmason/cart-service/interfaces/cartItem.js";
import { ICartItemToAdd } from "@wbmason/cart-service/interfaces/cartItemToAdd.js";
import { isSKUGroupOrCategoryId, isYesOrTrue } from "../utilities/utilities.js";
import { MyListService } from "@wbmason/my-list-service";
import { IListItemAction } from "@wbmason/my-list-service/interfaces/IListItemAction.js";
import { ActionType } from "@wbmason/my-list-service/constants/enums.js";
import { CartService } from "@wbmason/cart-service";
import { DigitalDataService } from "@wbmason/digital-data-service";
import { AddToCartMethod } from "@wbmason/digital-data-service/constants/enums.js";

export interface IProductCardProps {
  configProps: Record<string, string>;
  productInfo: IProductInfo;
  itemIndicators: IIndicatorIcon[];
  itemAvailability?: IItemAvailability;
  maxLength: number;
  displayAddToCart: boolean;
  displayListPrice?: boolean;
  hideRelevanceScore?: boolean;
  preventListPrice?: boolean;
  hasMultipleSupersedes?: boolean;
  supersedeClick?: (productInfo: IProductInfo) => void;
  addToCartMethod?: AddToCartMethod;
  productDetailsUrl?: string;
  productCustomizeUrl?: string;
  ListItemsInCart?: number;
  enableAllSalesFinal?: boolean;
  hideProductPrice?: boolean;
  asLowAsHack?: boolean;
  order?: number;
  individualUnitData?: IIndividualUnitData;
  displayIndividualUnitPrice?: boolean;
  supersedeItems?: ISupercedeProduct[];
  lineLevelCouponDisplayType?: LineLevelCouponDiscDisplay;
  qtyBreaks?: IQuantityBreak[];
  isSkuGroupSearch?: boolean;
  cartItems?: ICartItem[];
  closeCallback?: () => void;
  productImagesLoaded?: boolean;
  skuTable?: boolean;
  skuGroupId?: string;
  swapSavings?: number;
  addFeaturedProduct?: () => void;
  hostUrl: string;
}

export const ProductCard: React.FC<IProductCardProps> = (
  props: IProductCardProps
) => {
  const [qtyBreaksIsOpen, setqtyBreaksIsOpen] = useState<boolean>(false);
  const [swapSavings, setSwapSavings] = useState<number>(0);
  const [qtyBreaks, setQtyBreaks] = useState<IQuantityBreak[] | undefined>([]);
  const [addToCartError, setAddToCartError] = useState("");

  const toggleQtyBreaks = () => {
    setqtyBreaksIsOpen((val) => !val);
  };

  const [quantity, setQuantity] = useState({
    selectedValue: props.productInfo.qty || 1,
    selectedText: props.productInfo.qty?.toString() || "1",
  });

  const setValue = (selectedValue: number, selectedText: string) => {
    if (quantity.selectedValue !== selectedValue) {
      setQuantity({ selectedValue: selectedValue, selectedText: selectedText });
    }
  };

  const AddItemSelected = (qty: string) => {
    setQuantity({ selectedValue: Number(qty), selectedText: qty.toString() });
    const itemsToAdd: ICartItemToAdd[] = [
      {
        itemIdentifier: props.productInfo.itemIdentifier,
        qty: Number(qty),
        notes: "",
        isPostSupercedeValidation: false,
      },
    ];
    CartService.addCartItems(itemsToAdd, itemsToAdd.length === 1).subscribe(
      () => {
        props.addFeaturedProduct && props.addFeaturedProduct();
      },
      (error) => {
        setAddToCartError(error);
        props.closeCallback && props.closeCallback();
      }
    );
  };

  const options = {
    favSelected: false,
    setFavorite:
      typeof window !== "undefined" && isSKUGroupOrCategoryId()
        ? undefined
        : () => {
            MyListService.emitItemsToAdd(
              [
                {
                  itemIdentifier: props.productInfo.itemIdentifier,
                  qty: quantity.selectedValue,
                  itemPosition: 0,
                } as IListItemAction,
              ],
              props.addToCartMethod !== AddToCartMethod.AddToCartSlideIn
                ? ActionType.AddToListFromResults
                : undefined
            );
            if (
              props.addToCartMethod === AddToCartMethod.AddToCartSlideIn &&
              props.closeCallback
            ) {
              props.closeCallback();
            }
          },
    manageList: props.addToCartMethod !== AddToCartMethod.AddToCartSlideIn,
    itemSelected: setValue,
    AddItemSelected: AddItemSelected,
    setLength: props.maxLength,
    tooltipText: "Add Note",
    list: [
      { value: 1, text: 1 },
      { value: 2, text: 2 },
      { value: 3, text: 3 },
      { value: 4, text: 4 },
      { value: 5, text: 5 },
      { value: 6, text: 6 },
      { value: 7, text: 7 },
      { value: 8, text: 8 },
      { value: 9, text: 9 },
      { value: 10, text: 10 },
    ],
  };

  useEffect(() => {
    const qb: IQuantityBreak[] | undefined = props.qtyBreaks?.filter((x) => {
      return (
        x.itemNumber == props.productInfo.itemIdentifier.id &&
        x.unitId == props.productInfo.itemIdentifier.unitId
      );
    });
    setQtyBreaks(qb);
  }, [props.qtyBreaks, props.productInfo]);

  const itemClicked = () => {
    const itemPosition = props.order || 0;
    const skuGroupId = props.skuTable ? props.skuGroupId || "" : "";
    DigitalDataService.setItemClickedEvent(skuGroupId, itemPosition + 1);
  };

  useEffect(() => {
    if (props.supersedeItems && props.supersedeItems.length > 0) {
      const relatedProducts: IPricedProduct[] = props.supersedeItems[0].relatedProducts.filter(
        (relProd) => {
          return (
            relProd.itemId == props.productInfo.itemId &&
            relProd.unitId == props.productInfo.unitId
          );
        }
      );
      const savings =
        relatedProducts.length > 0
          ? props.supersedeItems[0].sourceProduct.sellPrice -
            relatedProducts[0].sellPrice
          : swapSavings;
      setSwapSavings(savings);
    }
  }, [props.supersedeItems, props.productInfo, swapSavings]);

  const skuGroupAddToCartEnabled = useMemo(
    () => isYesOrTrue(props.configProps[ConfigProps.EnableSkuGroupAddToCart]),
    [props.configProps]
  );

  return (
    <div className="product-card">
      <WBMCard>
        <div className="row no-gutters">
          {props.productImagesLoaded ? (
            <div className="col-auto">
              <div className="product-image mx-auto">
                <a href={props.productDetailsUrl} onClick={itemClicked}>
                  <ProductImage
                    imgUrl={
                      props.productInfo.imageUrl
                        ? props.productInfo.imageUrl
                        : props.configProps[ConfigProps.NoImageAvailableImage]
                    }
                    className="image-container"
                    altText={props.productInfo.itemId}
                  />
                  {props.ListItemsInCart && (
                    <div className="list-items-in-cart">
                      <span
                        className="material-icons suspense-cart"
                        aria-label="shopping_cart"
                      >
                        shopping_cart
                      </span>
                      {props.ListItemsInCart} In Cart
                    </div>
                  )}
                </a>
              </div>
            </div>
          ) : (
            <div className="product-img skeleton"></div>
          )}
          <div className="col product-content">
            <ProductName
              brandName={props.productInfo.brandName}
              description={props.productInfo.description}
              itemIndicators={props.itemIndicators}
              itemIdentifier={props.productInfo.itemIdentifier}
              productHREF={props.productDetailsUrl}
              displayId={props.productInfo.itemId}
              textBold={true}
              iconSize={18}
              onClickProductName={itemClicked}
              allSalesFinal={
                props.enableAllSalesFinal &&
                props.productInfo.webNonReturnable ? (
                  <div className="badge badge-pill badge-warning non-returnable">
                    {props.configProps[ConfigProps.AllSalesFinalText]}
                  </div>
                ) : null
              }
            />
            <div className="pricing">
              {!props.hideProductPrice && (
                <>
                  <ProductPrice
                    unitPrice={props.productInfo.unitPrice}
                    unitId={props.productInfo.unitId}
                    asLowAs={props.asLowAsHack}
                    individualUnitData={props.individualUnitData}
                    displayUnitPricing={props.displayIndividualUnitPrice}
                    salesPrice={props.productInfo.salesPrice}
                    lineLevelCouponDisplayType={
                      props.swapSavings
                        ? LineLevelCouponDiscDisplay.Dollars
                        : props.lineLevelCouponDisplayType
                    }
                    lineLevelCouponSavings={
                      props.swapSavings
                        ? props.swapSavings > 0
                          ? { savings: props.swapSavings }
                          : null
                        : props.productInfo?.lineLevelCouponSavings &&
                          props.productInfo.lineLevelCouponSavings.savings > 0
                        ? props.productInfo.lineLevelCouponSavings
                        : null
                    }
                    displayListPrice={
                      !props.preventListPrice
                        ? isYesOrTrue(
                            props.configProps[ConfigProps.DisplayListPrice]
                          )
                        : false
                    }
                    listPrice={props.productInfo.listPrice}
                    qtyBreaks={
                      qtyBreaks &&
                      qtyBreaks.length > 0 && (
                        <QtyBreaksDropdown
                          title={"Buy More and Save"}
                          expand={qtyBreaksIsOpen}
                          small
                          onClick={toggleQtyBreaks}
                        />
                      )
                    }
                    oncePerCustomer={props.productInfo.oncePerCustomer}
                    qtyLimit={props.productInfo.limitQty}
                    couponCustomerLimitMessage={
                      props.configProps[
                        ConfigProps.CouponCustomerLimitMessage
                      ] || undefined
                    }
                    couponQtyLimitMessage={
                      props.configProps[ConfigProps.CouponQtyLimitMessage] ||
                      undefined
                    }
                    displayFootnotes={true}
                  />
                </>
              )}
            </div>
            <div className="availibility">
              <div className="row no-gutters">
                {props.itemAvailability && (
                  <ProductAvailability
                    availabilityInfo={props.itemAvailability}
                    hostUrl={props.hostUrl}
                  />
                )}
              </div>
            </div>

            {props.productInfo.showBuyRightImage &&
              !props.hasMultipleSupersedes && (
                <div className="mt-3 buy-right">
                  <img
                    src="https://images.wbmason.com/240/buyrightITEM.jpg"
                    alt="Buy Right"
                    width="75px"
                    height="18px"
                  />
                </div>
              )}
          </div>
        </div>
        <div className="anchor">
          <div className="product-spacer">
            -----------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
          </div>
        </div>
        {props.displayAddToCart &&
          ((props.isSkuGroupSearch && skuGroupAddToCartEnabled) ||
            !props.isSkuGroupSearch) && (
            <>
              {props.productInfo?.purchaseProcess == PurchaseProcess.NavInk ? (
                <CustomizeButton
                  permitPurchase={props.productInfo?.permitPurchase}
                  preventPurchase={props.productInfo?.preventPurchase}
                  restrictedLink={props.configProps[ConfigProps.RestrictedLink]}
                  restrictedMessage={
                    props.configProps[ConfigProps.RestrictedMessage]
                  }
                  productCustomizeUrl={props.productCustomizeUrl}
                  itemClicked={itemClicked}
                />
              ) : (
                <AddToCart
                  dataValues={options}
                  permitPurchase={props.productInfo.permitPurchase}
                  preventPurchase={props.productInfo.preventPurchase}
                  restrictedLink={props.configProps[ConfigProps.RestrictedLink]}
                  restrictedMessage={
                    props.configProps[ConfigProps.RestrictedMessage]
                  }
                  slideInfo={"CLOSE"}
                  addedToCart={props.cartItems}
                  error={addToCartError}
                  isSkuGroupSearch={props.isSkuGroupSearch}
                  supercedeItems={props.supersedeItems}
                  itemIdentifier={props.productInfo.itemIdentifier}
                  productCard={true}
                  inputIdentifier={`slideout-${props.productInfo.itemId}-${props.productInfo.unitId}-${props.order}`}
                  inputLabel={`slideout-${props.productInfo.itemId}-${props.productInfo.unitId}`}
                />
              )}
            </>
          )}
        {props.isSkuGroupSearch &&
          !skuGroupAddToCartEnabled &&
          props.productInfo?.preventPurchase && (
            <div className="row no-gutters">
              <div className="add-to-cart-generic d-flex justify-content-center">
                <div className="unavailable-container d-flex justify-content-center align-items-center">
                  <div className="font-weight-bold account-unavailable">
                    Not available for purchase at this time.
                  </div>
                </div>
              </div>
            </div>
          )}
        {props.isSkuGroupSearch &&
          !skuGroupAddToCartEnabled &&
          !props.productInfo?.preventPurchase && (
            <a
              href={props.productDetailsUrl}
              onClick={itemClicked}
              className="w-100 wbm-button btn-link"
            >
              <Button className="col btn-primary">View Details</Button>
            </a>
          )}
        {props.hasMultipleSupersedes && (
          <div
            className={`row no-gutters supercede-row ${
              props.productInfo.showBuyRightImage ? "buy-right" : ""
            }`}
          >
            <div className="col-md-4 col-12">
              {props.productInfo.showBuyRightImage && (
                <img
                  className="buy-right-image"
                  src="https://images.wbmason.com/240/buyrightITEM.jpg"
                  alt="Buy Right"
                  width="75px"
                  height="18px"
                />
              )}
            </div>
            <div className="col-md-7 col-8">
              <Button
                className="btn-primary replace-button"
                onClick={() => {
                  if (props.supersedeClick) {
                    props.supersedeClick(props.productInfo);
                  }
                }}
              >
                REPLACE SELECTION
              </Button>
            </div>
          </div>
        )}
        {isYesOrTrue(props.configProps[ConfigProps.DisplayRelevanceScore]) &&
          !props.hideRelevanceScore && (
            <div className="green-text">
              Relevance Score: {props.productInfo.relevanceScore}
            </div>
          )}
        {qtyBreaksIsOpen && qtyBreaks && qtyBreaks.length > 0 && (
          <QuantityBreak
            itemIdentifier={props.productInfo.itemIdentifier}
            individualUnitData={props.individualUnitData}
            displayUnitPricing={isYesOrTrue(
              props.configProps[ConfigProps.DisplayIndividualUnitPrice]
            )}
            unitPrice={props.productInfo.unitPrice}
            selectedQuantity={props.productInfo.qty}
            quantityBreaks={qtyBreaks}
            onClick={toggleQtyBreaks}
            baseOncePerCustomer={props.productInfo.oncePerCustomer}
            baseQtyLimit={props.productInfo.limitQty}
            couponCustomerLimitMessage={
              props.configProps[ConfigProps.CouponCustomerLimitMessage] ||
              undefined
            }
            couponQtyLimitMessage={
              props.configProps[ConfigProps.CouponQtyLimitMessage] || undefined
            }
          />
        )}
      </WBMCard>
    </div>
  );
};

export default ProductCard;
